import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, BookOpen, Check, CircleDot, FileSearch, FileText, RefreshCw } from 'lucide-react';
import { StatusChip } from '../../abf/components/StatusChip';
import TemplateReadBanner from '../../documentTemplates/TemplateReadBanner';
import { ViewportToast, type ActionFeedbackState } from '../../abf/components/ActionFeedback';
import EvidencePanel from '../../abf/onboarding/review/EvidencePanel';
import {
  DecisionBar, Header, Journey, Note, Readable, SmallButton, SummaryGrid, Surface, Work,
} from '../../abf/onboarding/review/contractReviewStyles';
import { blotterLoanPath, loan360Path } from '../../../utils/loanNavigation';
import {
  type AcquisitionTicket, type AgreementReviewRecord, confirmAgreementSource, getAcquisitionTicket, getAgreementDocument,
  getAgreementReviewRecord, locateAgreementSources, reviewErrorMessage,
} from './agreementReviewApi';
import {
  type AgreementRow, assetLane, buildAgreementGroups, documentKind, documentWord, readText,
  sourceChip, sourceNote, sourceSettled, summaryFacts, toEvidenceRow,
} from './agreementReviewModel';
import { type ReviewTarget, reviewJourney } from './reviewJourney';
import AgreementRecordList from './AgreementRecordList';
import SourceConfirmForm from './SourceConfirmForm';

const DOCUMENT_PROCESSING = '/middle-office?tab=document-processing';

/** The shared strip is laid out for three stops; a private credit agreement has four. */
const Stops = styled(Journey)<{ $count: number }>`
  grid-template-columns: repeat(${p => p.$count}, 1fr);
  @media (max-width: 1000px) { grid-template-columns: repeat(2, 1fr); li:nth-child(odd) { border-left: 0; } li:nth-child(n + 3) { border-top: 1px solid #29374b; } }
  @media (max-width: 700px) { grid-template-columns: 1fr; li { border-left: 0; } }
`;

/**
 * The source panel stays in view beside a long list of terms. Pinned content taller than the screen
 * cannot be scrolled to, so the panel scrolls inside itself in the space above the next-step bar.
 */
const SourceColumn = styled.div`
  max-height: calc(100vh - 125px - 104px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 4px;
  @media (max-width: 1150px) { max-height: none; overflow: visible; padding-right: 0; }
`;

const Coverage = styled(Note)`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px 16px;
  margin-bottom: 14px;
  label { display: inline-flex; align-items: center; gap: 7px; color: #c5d4e6; font-size: 13px; cursor: pointer; }
  input { accent-color: #67c6f4; }
  .controls { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
`;

/**
 * A loan document reviewed against its source: every extracted term beside the passage it was
 * read from, with the document opened at that page. The reviewer can point a value at its page;
 * approval, import and the ticket stay in the workflows that own them, and this page links to each.
 */
const AgreementReviewPage: React.FC = () => {
  const { jobId = '' } = useParams();
  const [linkParams] = useSearchParams();
  // A link from elsewhere (a covenant on the loan page) can ask for one line by name.
  const requestedTerm = (linkParams.get('term') || '').trim().toLowerCase();
  // A link from Ask Axiom names the stated value by its field path; the row holding it opens.
  const requestedPointer = (linkParams.get('pointer') || '').trim();
  const navigate = useNavigate();
  const [review, setReview] = useState<AgreementReviewRecord | null>(null);
  const [ticket, setTicket] = useState<AcquisitionTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(false);
  const [paneWidth, setPaneWidth] = useState(54);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const sourceUrl = useRef<string | null>(null);
  const alive = useRef(true);
  const workRef = useRef<HTMLElement>(null);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; if (sourceUrl.current) URL.revokeObjectURL(sourceUrl.current); };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await getAgreementReviewRecord(jobId);
      if (!alive.current) return;
      setReview(next); setError(null);
      // Only a private credit agreement creates an acquisition ticket on import. The ticket is
      // context for the next step; failing to read it must not block the review.
      const createsTicket = documentKind(next.document_type) === 'AGREEMENT' && assetLane(next.document_type) === 'PRIVATE_CREDIT';
      if (createsTicket && next.linked_security_id) {
        try {
          const found = await getAcquisitionTicket(next.linked_security_id);
          if (alive.current) setTicket(found);
        } catch { if (alive.current) setTicket(null); }
      } else setTicket(null);
    } catch (failure) {
      if (alive.current) setError(reviewErrorMessage(failure, 'The document could not be loaded. Please retry.'));
    } finally { if (alive.current) setLoading(false); }
  }, [jobId]);
  useEffect(() => { void load(); }, [load]);

  const groups = useMemo(() => review ? buildAgreementGroups(review) : [], [review]);
  const rows = useMemo(() => groups.flatMap(group => group.rows), [groups]);
  // Open on a term whose passage is confirmed, so the first thing shown is a source.
  const selected: AgreementRow | null = rows.find(row => row.uid === selectedUid)
    || (requestedPointer ? rows.find(row => row.uid === requestedPointer || row.sourcePointer === requestedPointer)
      || rows.filter(row => requestedPointer.startsWith(`${row.uid}/`)).sort((a, b) => b.uid.length - a.uid.length)[0] : undefined)
    || (requestedTerm ? rows.find(row => row.label.trim().toLowerCase() === requestedTerm) : undefined)
    || rows.find(row => sourceSettled(row.citation)) || rows[0] || null;

  const loadDocument = useCallback(async () => {
    if (sourceUrl.current) return sourceUrl.current;
    const blob = await getAgreementDocument(jobId);
    if (!alive.current) throw new Error('Review closed');
    sourceUrl.current = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    return sourceUrl.current;
  }, [jobId]);

  const select = (row: AgreementRow) => {
    setSelectedUid(row.uid);
    if (window.matchMedia('(max-width: 1150px)').matches) workRef.current?.scrollIntoView({ block: 'start' });
  };

  const locate = async () => {
    setLocating(true);
    try {
      const before = rows.filter(row => sourceSettled(row.citation)).length;
      const next = await locateAgreementSources(jobId);
      if (!alive.current) return;
      setReview(next);
      const found = buildAgreementGroups(next).flatMap(group => group.rows).filter(row => sourceSettled(row.citation)).length - before;
      setFeedback({
        tone: found > 0 ? 'success' : 'info',
        message: found > 0
          ? `${found} more source${found === 1 ? '' : 's'} found and confirmed in the document.`
          : 'No further passages were found. The remaining values may not be stated word for word.',
      });
    } catch (failure) {
      if (alive.current) setFeedback({ tone: 'error', message: reviewErrorMessage(failure, 'The document could not be searched. Try again.') });
    } finally { if (alive.current) setLocating(false); }
  };

  const confirmSource = async (row: AgreementRow, page: number | null, clause: string | null) => {
    try {
      const next = await confirmAgreementSource(jobId, row.sourcePointer, page, clause);
      if (!alive.current) return;
      setReview(next);
      setFeedback({ tone: 'success', message: `Source confirmed for ${row.label}${page ? `: page ${page}` : ''}.` });
    } catch (failure) {
      throw new Error(reviewErrorMessage(failure, 'The source was not saved. Try again.'));
    }
  };

  if (!review) return <Surface>
    <Note role={error ? 'alert' : 'status'}>
      <p>{error || 'Loading the document and its sources…'}</p>
      {error && <SmallButton type="button" onClick={() => void load()} disabled={loading}>Retry</SmallButton>}
      <p><Link to={DOCUMENT_PROCESSING} style={{ color: '#a6d8f5' }}>Back to Document Processing</Link></p>
    </Note>
  </Surface>;

  const lane = assetLane(review.document_type);
  const word = documentWord(review.document_type, readText(review, ['notice_event', 'event_type']));
  // Sources are counted over the lines the reviewer sees: a covenant is one line with one passage.
  const confirmed = rows.filter(row => sourceSettled(row.citation)).length;
  const unconfirmed = rows.length - confirmed;
  const journey = reviewJourney(review, ticket, { confirmed, total: rows.length });
  const loanId = review.linked_loan_record_id;
  const cusip = readText(review, ['loan_identifiers', 'cusip']);
  const facilityCode = readText(review, ['loan_identifiers', 'loan_id']) || readText(review, ['deal_code']);
  const title = readText(review, ['borrower', 'legal_name']) || readText(review, ['deal_name']) || review.document_name || 'Loan document';
  const facilityHref = loanId ? loan360Path(lane, loanId) : null;

  const follow = (target: ReviewTarget) => {
    if (target === 'APPROVE') navigate(`${DOCUMENT_PROCESSING}&reviewJob=${encodeURIComponent(jobId)}`);
    else if (target === 'TICKET') navigate(cusip ? blotterLoanPath(cusip) : '/bond-transactions');
    else if (target === 'FACILITY' && facilityHref) navigate(facilityHref);
    else navigate(DOCUMENT_PROCESSING);
  };

  const chip = selected ? sourceChip(selected.citation) : undefined;

  return <Surface>
    <Header>
      <Link className="back" to={facilityHref || DOCUMENT_PROCESSING}>
        <ArrowLeft size={14} /> {facilityHref ? (lane === 'LEVERAGED_LOAN' ? 'Loan' : 'Facility') : 'Document Processing'}
      </Link>
      <div className="heading">
        <div>
          <div className="eyebrow">{journey.eyebrow}</div>
          <h1>{title}</h1>
          <div className="meta">
            {facilityCode && <span>{facilityCode}</span>}
            {cusip && <span>CUSIP {cusip}</span>}
            <span><FileText size={14} />{review.document_name || 'Document'}{review.document.page_count ? ` · ${review.document.page_count} page${review.document.page_count === 1 ? '' : 's'}` : ''}</span>
          </div>
        </div>
        <div className="actions">
          <StatusChip tone={journey.state.tone} label={journey.state.label} size="md" />
          <SmallButton type="button" onClick={() => void load()} disabled={loading || locating} aria-label="Refresh">
            <RefreshCw size={14} />{loading ? 'Refreshing…' : 'Refresh'}
          </SmallButton>
        </div>
      </div>
    </Header>

    {error && <Note role="alert" style={{ marginBottom: 16 }}>{error} Displayed information may be out of date.</Note>}

    <Stops $count={journey.stops.length} aria-label="Document progress">
      {journey.stops.map((stop, index) => (
        <li key={stop.title} data-current={index === journey.current} aria-current={index === journey.current ? 'step' : undefined}>
          <span className={`step-icon ${stop.done ? 'done' : ''}`}>{stop.done ? <Check size={15} /> : <CircleDot size={15} />}</span>
          <div><strong>{index + 1}. {stop.title}</strong><small>{stop.detail}</small></div>
        </li>
      ))}
    </Stops>

    <SummaryGrid aria-label="Document summary">
      {summaryFacts(review).map(fact => <div key={fact.label}><span>{fact.label}</span><strong>{fact.value}</strong></div>)}
      <div><span>Sources</span><strong>{confirmed} of {rows.length} confirmed in the {word}</strong></div>
    </SummaryGrid>

    {/* F-PCP-039: the template's verdict on this read sits where the reviewer decides; a failed read says so here. */}
    {review.template_validation && <TemplateReadBanner validation={review.template_validation} />}
    {rows.length === 0 && <Coverage role="alert"><div><strong>Nothing was extracted from this document</strong>
      <p>There is no record here to approve. If this job shows as approved or booked, its read was replaced after approval; re-read the document so the record and the approval are the same thing.</p></div></Coverage>}

    <Work ref={workRef} style={{ scrollMarginTop: 125 }} aria-label="Terms and their sources">
      <Coverage role="status">
        <div>
          <strong>{unconfirmed === 0 ? 'Every stated value has a confirmed source'
            : `${unconfirmed} value${unconfirmed === 1 ? '' : 's'} without a confirmed source`}</strong>
          <p>{!review.document.available
            ? 'The document file is no longer on file, so quotes cannot be checked against it.'
            : `Confirmed means the quoted wording was found on the page named, or a reviewer confirmed the source. Select any row to read it in the ${word}.`}</p>
        </div>
        <div className="controls">
          {unconfirmed > 0 && <label>
            <input type="checkbox" checked={onlyUnconfirmed} onChange={event => setOnlyUnconfirmed(event.target.checked)} />
            Show only these
          </label>}
          {unconfirmed > 0 && review.document.available && <SmallButton type="button" onClick={() => void locate()} disabled={locating}>
            <FileSearch size={14} />{locating ? 'Searching the document…' : 'Find the missing sources'}
          </SmallButton>}
          <label className="pane-size" style={{ margin: 0 }}>Terms / source width
            <input type="range" min="38" max="66" value={paneWidth} aria-label="Terms pane width"
              onChange={event => setPaneWidth(Number(event.target.value))} />
          </label>
        </div>
      </Coverage>

      <div className="work-pair" style={{ '--review-pane': `${paneWidth}%` } as React.CSSProperties}>
        <Readable>
          <AgreementRecordList groups={groups} selectedUid={selected?.uid || null} onSelect={select} onlyUnconfirmed={onlyUnconfirmed} documentNoun={word} />
        </Readable>
        <SourceColumn>
          <div className="source-heading"><BookOpen size={14} />Source for {selected?.label || 'the selected term'}</div>
          <Readable>
            <EvidencePanel row={selected ? toEvidenceRow(selected) : null} documentName={review.document_name}
              canShowDocument={review.document.available} loadDocument={loadDocument} versionId={0}
              chip={chip} sourceNote={selected ? sourceNote(selected.citation) : undefined} documentNoun={word}
              citationControls={selected && <SourceConfirmForm row={selected} pageCount={review.document.page_count}
                onConfirm={(page, clause) => confirmSource(selected, page, clause)} />} />
          </Readable>
        </SourceColumn>
      </div>
    </Work>

    <DecisionBar aria-label="Next step">
      <div><strong>{journey.next.headline}</strong><span>{journey.next.reason}</span></div>
      <SmallButton type="button" onClick={() => follow(journey.next.target)}>{journey.next.action} <ArrowRight size={14} /></SmallButton>
    </DecisionBar>
    <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
  </Surface>;
};

export default AgreementReviewPage;
